import React from 'react';
import { Card, CardContent } from "../../ui/card";
import { EditableField } from "../../action-item/EditableField";
import { meetingNotesService } from "../../../services/supabase/meeting-notes";
import { parseJsonSafely } from "../../../lib/formatting-utils";

export interface Decision {
  decision: string;
  implications: string;
  supporting_quote: string;
}

export interface DifferingOpinion {
  disagreement: string;
  supporting_quotes: string[];
}

export interface DecisionData {
  main_decisions_resolutions_agreements: Decision[];
  differing_opinions: DifferingOpinion[];
}

interface DecisionsSectionProps {
  decisions: DecisionData | Decision[] | Record<string, any>[] | null;  // Updated to allow Record<string, any>[]
  meetingId?: string;
  userId?: string;
}

export function DecisionsSection({ decisions, meetingId, userId }: DecisionsSectionProps) {
  const handleUpdate = async (value: string) => {
    if (!meetingId || !userId) return;
    
    try {
      await meetingNotesService.updateField(meetingId, userId, 'decisions', JSON.parse(value));
    } catch (error) {
      console.error('Error updating decisions:', error);
    }
  };

  // Handle both formats: direct array or wrapped in DecisionData structure
  const normalizeDecisions = (data: any) => {
    if (!data) return null;
    
    // If it's an array, wrap it in the expected structure
    if (Array.isArray(data)) {
      return {
        main_decisions_resolutions_agreements: data,
        differing_opinions: []
      };
    }
    
    // If it's already in the correct format, return as is
    if (data.main_decisions_resolutions_agreements) {
      return data;
    }
    
    return null;
  };

  const parsedDecisions = normalizeDecisions(parseJsonSafely(decisions));

  const renderDecision = (decision: Decision) => (
    <div className="mb-8">
      <p className="decision-text">{decision.decision}</p>
      <p className="decision-text">{decision.implications}</p>
      <span className="decision-quote">{decision.supporting_quote}</span>
    </div>
  );

  const renderDifferingOpinion = (opinion: DifferingOpinion) => (
    <div className="differing-opinion">
      <p className="decision-text">{opinion.disagreement}</p>
      {opinion.supporting_quotes.map((quote, index) => (
        <span key={index} className="decision-quote">{quote}</span>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Key Decisions & Differing Opinions</h3>
      <Card>
        <CardContent className="p-6">
          {parsedDecisions ? (
            <div>
              {parsedDecisions.main_decisions_resolutions_agreements?.map((decision, index) => (
                <div key={index}>
                  {renderDecision(decision)}
                </div>
              ))}
              
              {parsedDecisions.differing_opinions?.length > 0 && (
                <>
                  <h4 className="text-md font-semibold mt-8 mb-4">Differing Opinions</h4>
                  {parsedDecisions.differing_opinions.map((opinion, index) => (
                    <div key={index}>
                      {renderDifferingOpinion(opinion)}
                    </div>
                  ))}
                </>
              )}
            </div>
          ) : (
            <EditableField
              value={JSON.stringify(decisions || [], null, 2)}
              onChange={handleUpdate}
              type="textarea"
              placeholder="No decisions recorded..."
              showToast={true}
              className="min-h-[200px]"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}